import React, { useState, useMemo } from 'react';
import {
  Download,
  FileSpreadsheet,
  FileDown,
  Loader2,
  Users,
  DollarSign,
  Calendar,
  AlertTriangle,
  Building2,
  FileText,
  Shield,
  Clock,
  ChevronsUp,
  ChevronsDown,
} from 'lucide-react';
import toast from '../../../utils/toast';
import api from '../../../services/api';
import { useDepartments } from '../../../hooks/useConfigData';
import DatePickerField from '../../../components/UI/DatePickerField';
import MultiPillSelect from '../../../components/UI/MultiPillSelect';
import PillToggle from '../../../components/UI/PillToggle';
import LeaveTypeBadge from '../../../components/UI/LeaveTypeBadge';
import AccordionSection from '../../../components/UI/AccordionSection';
import { fmtCurrency, fmtDays, fmtDate } from '../../../utils/pdfReportTemplate';
import {
  exportPayrollLeaveDeductionExcel,
  exportPayrollLeaveDeductionCSV,
  exportPayrollLeaveDeductionPDF,
} from '../../../utils/payrollLeaveDeductionExport';

function getLastMonthDate() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() - 1, 1);
}

function getMonthStart(d) {
  if (!d) return null;
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function getMonthEnd(d) {
  if (!d) return null;
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

function toISODateString(d) {
  if (!d) return null;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatPeriodLabel(start, end) {
  if (!start || !end) return '';
  const s = new Date(start);
  const e = new Date(end);
  if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
    return s.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  }
  return `${s.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} — ${e.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
}

const CLASSIFICATION_OPTIONS = [
  { value: 'staff', label: 'Staff' },
  { value: 'worker', label: 'Worker' },
];

const CARD_COLOR_MAP = {
  red: { bg: 'bg-red-100', text: 'text-red-600' },
  amber: { bg: 'bg-amber-100', text: 'text-amber-600' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-600' },
  teal: { bg: 'bg-teal-100', text: 'text-teal-600' },
};

const SummaryCard = ({ label, value, sub, icon: Icon, color }) => {
  const c = CARD_COLOR_MAP[color] || CARD_COLOR_MAP.red;
  return (
    <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
      <div className="flex items-center">
        <div className={`w-9 h-9 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${c.bg}`}>
          <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${c.text}`} />
        </div>
        <div className="ml-2 sm:ml-4 min-w-0">
          <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{value}</p>
          <p className="text-xs sm:text-sm text-gray-600 truncate">{label}</p>
          {sub && <p className="text-xs text-gray-500 mt-1 truncate">{sub}</p>}
        </div>
      </div>
    </div>
  );
};

const DetailMetaCell = ({ label, value, emphasis = false }) => (
  <div className="rounded-lg bg-gray-50 px-4 py-3">
    <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</div>
    <div className={`mt-1 text-sm ${emphasis ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>
      {value}
    </div>
  </div>
);

const formatLeaveDates = (item) => {
  if (!item.leave_start_date || !item.leave_end_date) return '-';
  const isSameDay = item.leave_start_date === item.leave_end_date;
  const startHalf = item.leave_start_time_period === 'PM';
  const endHalf = item.leave_end_time_period === 'AM';
  if (isSameDay) {
    const session = startHalf ? ' (PM)' : endHalf ? ' (AM)' : '';
    return `${fmtDate(item.leave_start_date)}${session}`;
  }
  return `${fmtDate(item.leave_start_date)}${startHalf ? ' (PM)' : ''} — ${fmtDate(item.leave_end_date)}${endHalf ? ' (AM)' : ''}`;
};

const EmployeeRow = ({ employee, expanded, onToggle }) => {
  const unpaidDays = parseFloat(employee.subtotal_unpaid_days || 0);
  const nsDays = parseFloat(employee.subtotal_ns_days || 0);
  const totalDeduction = parseFloat(employee.total_deduction || 0);

  return (
    <AccordionSection
      expanded={expanded}
      onToggle={onToggle}
      contentVariant="flush"
      title={
        <span className="flex items-center gap-3 min-w-0">
          <span className="font-mono text-xs text-gray-500">{employee.staff_id}</span>
          <span className="font-medium text-gray-900 truncate">{employee.employee_name}</span>
          <span className="text-xs text-gray-500 truncate hidden sm:inline">{employee.department || '-'}</span>
        </span>
      }
      trailing={
        <span className="flex items-center gap-4 text-sm tabular-nums">
          {unpaidDays > 0 && (
            <span className="font-semibold text-gray-700">{fmtDays(unpaidDays)}d unpaid</span>
          )}
          {nsDays > 0 && (
            <span className="font-semibold text-teal-700">{fmtDays(nsDays)}d NS</span>
          )}
          <span className="font-semibold text-red-600">{fmtCurrency(totalDeduction)}</span>
        </span>
      }
    >
      <div className="border-b border-gray-100 bg-white px-4 py-4">
        <div className="mb-3 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 truncate">Deduction Worksheet</h3>
            <p className="text-xs text-gray-500 truncate">Payroll-impacting leave segments for this employee</p>
          </div>
          <div className="text-xs font-medium text-gray-500 flex-shrink-0 whitespace-nowrap">
            {employee.items.length} line item{employee.items.length !== 1 ? 's' : ''}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <DetailMetaCell
            label="Classification"
            value={employee.classification || '-'}
            emphasis
          />
          <DetailMetaCell
            label="Basic Salary"
            value={employee.basic_salary ? fmtCurrency(employee.basic_salary) : '-'}
          />
          <DetailMetaCell
            label="Daily Rate"
            value={employee.daily_rate ? fmtCurrency(employee.daily_rate) : '-'}
          />
        </div>
      </div>

      {/* Mobile cards (below md) */}
      <div className="md:hidden space-y-3 p-4">
        {employee.items.map((item, idx) => (
          <div key={idx} className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <LeaveTypeBadge leaveTypeName={item.leave_type_name || item.leave_type_display} />
                <p className="text-xs text-gray-500 mt-1 truncate">
                  {item.origin_leave_type === 'Direct' ? 'Direct' : `From ${item.origin_leave_type || '-'}`}
                </p>
              </div>
              <span className="text-sm font-semibold text-red-600 tabular-nums flex-shrink-0">
                {fmtCurrency(item.deduction_amount)}
              </span>
            </div>
            <div className="mt-3 space-y-1.5">
              <div className="flex items-start justify-between gap-3 text-sm">
                <span className="text-gray-500 flex-shrink-0">Leave Dates</span>
                <span className="text-gray-900 text-right min-w-0">{formatLeaveDates(item)}</span>
              </div>
              <div className="flex items-start justify-between gap-3 text-sm">
                <span className="text-gray-500 flex-shrink-0">Application Days</span>
                <span className="text-gray-900 text-right truncate min-w-0 tabular-nums">{fmtDays(item.total_leave_days)}</span>
              </div>
              <div className="flex items-start justify-between gap-3 text-sm">
                <span className="text-gray-500 flex-shrink-0">Deductible Days</span>
                <span className="text-gray-900 font-semibold text-right truncate min-w-0 tabular-nums">{fmtDays(item.days_in_period)}</span>
              </div>
            </div>
          </div>
        ))}
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center justify-between gap-3 text-xs font-semibold">
          <span className="uppercase tracking-wide text-gray-600">Employee Sub-total</span>
          <span className="flex items-center gap-4 tabular-nums">
            <span className="text-gray-900">
              {fmtDays(parseFloat(employee.subtotal_unpaid_days || 0) + parseFloat(employee.subtotal_ns_days || 0))}d
            </span>
            <span className="text-red-700">{fmtCurrency(employee.total_deduction)}</span>
          </span>
        </div>
      </div>
      {/* Desktop table (md and up) */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full min-w-[900px] table-fixed text-sm">
          <colgroup>
            <col className="w-[150px]" />
            <col className="w-[190px]" />
            <col className="w-[220px]" />
            <col className="w-[110px]" />
            <col className="w-[120px]" />
            <col className="w-[130px]" />
          </colgroup>
          <thead>
            <tr className="border-y border-red-200 bg-red-50 text-[11px] uppercase tracking-wide text-red-700">
              <th className="px-4 py-3 text-left font-semibold">Derived From</th>
              <th className="px-4 py-3 text-left font-semibold">Leave Type</th>
              <th className="px-4 py-3 text-center font-semibold">Leave Dates</th>
              <th className="px-4 py-3 text-right font-semibold">Application Days</th>
              <th className="px-4 py-3 text-right font-semibold">Deductible Days</th>
              <th className="px-4 py-3 text-right font-semibold">Deduction</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {employee.items.map((item, idx) => (
              <tr key={idx} className="table-row-dense">
                <td className="px-4 py-3 align-middle text-xs">
                  {item.origin_leave_type === 'Direct'
                    ? <span className="text-gray-400">Direct</span>
                    : <span className="text-gray-600">{item.origin_leave_type || '-'}</span>
                  }
                </td>
                <td className="px-4 py-3 align-middle text-gray-900">
                  <LeaveTypeBadge leaveTypeName={item.leave_type_name || item.leave_type_display} />
                </td>
                <td className="px-4 py-3 align-middle text-center text-xs text-gray-600 whitespace-nowrap">
                  {formatLeaveDates(item)}
                </td>
                <td className="px-4 py-3 align-middle text-right text-gray-600 tabular-nums">{fmtDays(item.total_leave_days)}</td>
                <td className="px-4 py-3 align-middle text-right font-semibold tabular-nums">{fmtDays(item.days_in_period)}</td>
                <td className="px-4 py-3 align-middle text-right font-semibold text-red-600 tabular-nums">
                  {fmtCurrency(item.deduction_amount)}
                </td>
              </tr>
            ))}
            <tr className="border-t-2 border-red-200 bg-red-50 text-xs font-semibold">
              <td colSpan={4} className="px-4 py-3 text-right uppercase tracking-wide text-gray-600">Employee Sub-total</td>
              <td className="px-4 py-3 text-right text-gray-900 tabular-nums">
                {fmtDays(parseFloat(employee.subtotal_unpaid_days || 0) + parseFloat(employee.subtotal_ns_days || 0))}
              </td>
              <td className="px-4 py-3 text-right text-red-700 tabular-nums">{fmtCurrency(employee.total_deduction)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </AccordionSection>
  );
};

const PayrollLeaveDeductionReport = () => {
  const [periodMode, setPeriodMode] = useState('monthly');
  const [monthDate, setMonthDate] = useState(() => getLastMonthDate());
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [departmentIds, setDepartmentIds] = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [exporting, setExporting] = useState(null);

  const { data: departments = [] } = useDepartments();

  const departmentOptions = useMemo(
    () => departments.map((d) => ({ value: String(d.id), label: d.department_name })),
    [departments],
  );

  const periodDates = useMemo(() => {
    if (periodMode === 'monthly' && monthDate) {
      return { start: getMonthStart(monthDate), end: getMonthEnd(monthDate) };
    }
    if (periodMode === 'custom' && customStart && customEnd) {
      return { start: customStart, end: customEnd };
    }
    return { start: null, end: null };
  }, [periodMode, monthDate, customStart, customEnd]);

  const canGenerate = periodDates.start && periodDates.end && !loading;

  const handleGenerate = async () => {
    if (!canGenerate) return;
    setLoading(true);
    try {
      const payload = {
        period_start: toISODateString(periodDates.start),
        period_end: toISODateString(periodDates.end),
      };
      if (departmentIds.length > 0) payload.department_ids = departmentIds.map(Number);
      if (classifications.length > 0) payload.classifications = classifications;

      const res = await api.post('/reporting/payroll-leave-deductions/', payload);
      setReportData(res.data);
      setExpandedRows({});

      const total = res.data.total_employees || 0;
      toast.success(`Report generated — ${total} employee${total !== 1 ? 's' : ''} with payroll-affecting leave`);

      if (res.data.errors?.length > 0) {
        toast.warning(`${res.data.errors.length} employee(s) had calculation errors`, { icon: '⚠️' });
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to generate report';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (staffId) => {
    setExpandedRows((prev) => ({ ...prev, [staffId]: !prev[staffId] }));
  };

  const expandAll = () => {
    if (!reportData?.employees) return;
    const all = {};
    reportData.employees.forEach((e) => { all[e.staff_id] = true; });
    setExpandedRows(all);
  };

  const collapseAll = () => setExpandedRows({});

  const summary = reportData?.summary;
  const employees = reportData?.employees || [];

  const allRowsExpanded = employees.length > 0 && employees.every((e) => expandedRows[e.staff_id]);
  const toggleAllRows = () => allRowsExpanded ? collapseAll() : expandAll();

  const handleExport = async (type) => {
    if (!reportData) return;
    setExporting(type);
    try {
      if (type === 'pdf') await exportPayrollLeaveDeductionPDF(reportData);
      else if (type === 'excel') await exportPayrollLeaveDeductionExcel(reportData);
      else if (type === 'csv') exportPayrollLeaveDeductionCSV(reportData);
    } catch (err) {
      console.error(`Export ${type} failed:`, err);
      toast.error(`Failed to export ${type.toUpperCase()}`);
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">Payroll Leave Deductions</h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">
            Leave applications affecting payroll — unpaid leave, NS leave, AL spillover
          </p>
        </div>
        {reportData && (
          <div className="flex items-center gap-2">
            <button onClick={() => handleExport('pdf')} disabled={!!exporting} className="btn btn-admin-secondary">
              {exporting === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
              PDF
            </button>
            <button onClick={() => handleExport('excel')} disabled={!!exporting} className="btn btn-admin-secondary">
              {exporting === 'excel' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
              Excel
            </button>
            <button onClick={() => handleExport('csv')} disabled={!!exporting} className="btn btn-admin-secondary">
              {exporting === 'csv' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileDown className="w-4 h-4 mr-2" />}
              CSV
            </button>
          </div>
        )}
      </div>

      {/* Parameters bar */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">Period Type</label>
          <PillToggle
            value={periodMode}
            onChange={setPeriodMode}
            options={[
              { id: 'monthly', label: 'Monthly' },
              { id: 'custom', label: 'Custom Range' },
            ]}
            variant="compact"
            theme="admin"
            ariaLabel="Period type"
          />
        </div>

        {periodMode === 'monthly' ? (
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Month</label>
            <DatePickerField
              theme="admin"
              selected={monthDate}
              onChange={(date) => setMonthDate(date)}
              dateFormat="MMMM yyyy"
              showMonthYearPicker
              maxDate={new Date()}
              placeholderText="Select month"
              wrapperClassName="w-52"
            />
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500">Start Date</label>
              <DatePickerField
                theme="admin"
                selected={customStart}
                onChange={(date) => setCustomStart(date)}
                maxDate={customEnd || new Date()}
                placeholderText="Start date"
                wrapperClassName="w-44"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500">End Date</label>
              <DatePickerField
                theme="admin"
                selected={customEnd}
                onChange={(date) => setCustomEnd(date)}
                minDate={customStart}
                maxDate={new Date()}
                placeholderText="End date"
                wrapperClassName="w-44"
              />
            </div>
          </>
        )}

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">Department</label>
          <MultiPillSelect
            options={departmentOptions}
            value={departmentIds}
            onChange={setDepartmentIds}
            allLabel="All Departments"
            icon={Building2}
            iconColor="text-red-600"
            theme="admin"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">Classification</label>
          <MultiPillSelect
            options={CLASSIFICATION_OPTIONS}
            value={classifications}
            onChange={setClassifications}
            allLabel="All Classifications"
            icon={Users}
            iconColor="text-red-600"
            theme="admin"
          />
        </div>

        <button
          onClick={handleGenerate}
          disabled={!canGenerate}
          className="btn btn-admin-primary"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
          Generate Report
        </button>
      </div>

      {/* Results */}
      {reportData && (
        <>
          {/* Period label */}
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Calendar className="w-4 h-4" />
            <span>Report period: <span className="font-medium text-gray-700">{formatPeriodLabel(reportData.period?.start, reportData.period?.end)}</span></span>
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <SummaryCard
              label="Employees Affected"
              value={summary?.total_employees || 0}
              icon={Users}
              color="red"
            />
            <SummaryCard
              label="Unpaid Leave Days"
              value={fmtDays(summary?.total_unpaid_days)}
              sub={summary?.total_unpaid_deduction ? `Deduction: ${fmtCurrency(summary.total_unpaid_deduction)}` : null}
              icon={Clock}
              color="amber"
            />
            <SummaryCard
              label="NS / ICT Days"
              value={fmtDays(summary?.total_ns_days)}
              sub={summary?.total_ns_deduction ? `Deduction: ${fmtCurrency(summary.total_ns_deduction)}` : null}
              icon={Shield}
              color="teal"
            />
            <SummaryCard
              label="Grand Total Deduction"
              value={fmtCurrency(summary?.grand_total_deduction)}
              icon={DollarSign}
              color="purple"
            />
          </div>

          {/* Employee list */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 space-y-2">
            <div className="flex items-center justify-between gap-2 mb-3">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 truncate min-w-0">
                Employee Breakdown
                <span className="ml-2 text-xs sm:text-sm font-normal text-gray-500">
                  ({employees.length}<span className="hidden sm:inline"> employee{employees.length !== 1 ? 's' : ''}</span>)
                </span>
              </h2>
              {employees.length > 0 && (
                <button
                  onClick={toggleAllRows}
                  className="icon-button-admin flex-shrink-0"
                  title={allRowsExpanded ? 'Collapse All' : 'Expand All'}
                  aria-label={allRowsExpanded ? 'Collapse All' : 'Expand All'}
                >
                  {allRowsExpanded ? <ChevronsUp className="w-4 h-4" /> : <ChevronsDown className="w-4 h-4" />}
                </button>
              )}
            </div>

            {employees.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p className="text-sm">No payroll-affecting leave found for this period.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {employees.map((emp) => (
                  <EmployeeRow
                    key={emp.staff_id}
                    employee={emp}
                    expanded={!!expandedRows[emp.staff_id]}
                    onToggle={() => toggleRow(emp.staff_id)}
                  />
                ))}
              </div>
            )}

            {/* Grand total bar */}
            {employees.length > 0 && (
              <div className="mt-3">
                <div className="px-4 py-3 bg-gray-800 text-white rounded-2xl text-sm font-semibold flex items-center justify-between">
                  <span>
                    Grand Total — {summary?.total_employees || 0} employee{summary?.total_employees !== 1 ? 's' : ''}
                  </span>
                  <span className="flex items-center gap-6 tabular-nums">
                    {parseFloat(summary?.total_unpaid_days || 0) > 0 && (
                      <span>{fmtDays(summary.total_unpaid_days)}d unpaid</span>
                    )}
                    {parseFloat(summary?.total_ns_days || 0) > 0 && (
                      <span>{fmtDays(summary.total_ns_days)}d NS</span>
                    )}
                    <span>{fmtCurrency(summary?.grand_total_deduction)}</span>
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Warnings */}
          {reportData.errors?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-semibold text-amber-800">Calculation Warnings</span>
              </div>
              <ul className="text-xs text-amber-700 space-y-1 ml-6 list-disc">
                {reportData.errors.map((err, idx) => (
                  <li key={idx}>{err.staff_id}: {err.error}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {/* Empty state */}
      {!reportData && !loading && (
        <div className="text-center py-16 text-gray-500">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <FileText className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Report Generated</h3>
          <p className="text-sm text-gray-500 max-w-md mx-auto">
            Select a payroll period and click "Generate Report" to view all leave applications
            that affect payroll — unpaid leave, NS leave, and AL spillover deductions.
          </p>
        </div>
      )}
    </div>
  );
};

export default PayrollLeaveDeductionReport;
